import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { DataGrid, type DataGridColumn } from '@/components/tables/DataGrid';
import { Select2 } from '@/components/forms/Select2';
import { TimePicker } from '@/components/forms/TimePicker';
import { useToast } from '@/components/common/toast';
import { formatNumber, formatDateTime } from '@/utils/format';
import { marketAbuseApi } from '@/api/services/marketAbuse';
import type { MarInstrumentDto } from '@/api/types/marketAbuse';

const extractTime = (iso: string | null) => {
  if (!iso) return '';
  const t = String(iso).indexOf('T');
  return t !== -1 ? String(iso).substring(t + 1, t + 9) : /^\d{2}:\d{2}/.test(String(iso)) ? String(iso) : '';
};
const coerceTime = (v: string): string | null => {
  if (!v) return null;
  const [h, m, s] = v.split(':');
  const d = new Date();
  d.setHours(Number(h) || 0, Number(m) || 0, Number(s) || 0, 0);
  return d.toISOString();
};
const numDisplay = (v: number, hasCondition: boolean) => (!hasCondition ? 'N/A' : formatNumber(v, (String(v).split('.')[1] || '').length));

interface EditState {
  enabled: boolean;
  from: string;
  to: string;
  avg: string;
  sensitivity: string;
}

export function MarInstrumentsTab({ onJump }: { onJump: (symbol: string, firstNotificationDate: string | null) => void }) {
  const toast = useToast();
  const query = useQuery({ queryKey: ['ma-instruments'], queryFn: () => marketAbuseApi.getInstruments(null) });
  const [editing, setEditing] = useState<string | null>(null);
  const [edit, setEdit] = useState<EditState | null>(null);
  const [busy, setBusy] = useState(false);

  const rows = query.data?.items ?? [];

  const startEdit = (r: MarInstrumentDto) => {
    setEditing(r.symbol);
    setEdit({ enabled: r.enabled, from: extractTime(r.fromSettlementTime), to: extractTime(r.toSettlementTime), avg: r.hasCondition ? String(r.averageDailyVolume) : '', sensitivity: r.hasCondition ? String(r.sensitivityPercent) : '' });
  };
  const save = async (symbol: string) => {
    if (!edit) return;
    setBusy(true);
    try {
      await marketAbuseApi.saveInstrumentCondition({
        symbol,
        enabled: edit.enabled,
        fromSettlementTime: coerceTime(edit.from),
        toSettlementTime: coerceTime(edit.to),
        averageDailyVolume: parseFloat(edit.avg.replace(/,/g, '')) || 0,
        sensitivityPercent: parseFloat(edit.sensitivity.replace(/,/g, '')) || 0,
      });
      toast.success('Instrument condition saved.');
      setEditing(null);
      await query.refetch();
    } catch {
      toast.error('Failed to save instrument condition.');
    } finally {
      setBusy(false);
    }
  };

  const isEd = (symbol: string) => editing === symbol;

  const columns: DataGridColumn<MarInstrumentDto>[] = [
    { key: 'symbol', title: 'Symbol', className: 'fw-semibold', value: (r) => r.symbol },
    {
      key: 'enabled',
      title: 'Enabled',
      className: 'text-center',
      value: (r) => (r.hasCondition ? (r.enabled ? 'Yes' : 'No') : 'N/A'),
      cell: (r) =>
        isEd(r.symbol) ? (
          <div style={{ minWidth: 90 }}>
            <Select2 searchable={false} allowClear={false} value={edit!.enabled ? 'true' : 'false'} options={[{ value: 'true', label: 'Yes' }, { value: 'false', label: 'No' }]} onChange={(v) => setEdit((s) => s && { ...s, enabled: v === 'true' })} />
          </div>
        ) : !r.hasCondition ? (
          <span className="text-muted">N/A</span>
        ) : (
          <span className={`badge fs-14 ${r.enabled ? 'bg-primary' : 'bg-dark'}`}>{r.enabled ? 'Yes' : 'No'}</span>
        ),
    },
    {
      key: 'from',
      title: 'From Settlement Time',
      value: (r) => extractTime(r.fromSettlementTime),
      cell: (r) =>
        isEd(r.symbol) ? (
          <TimePicker value={edit!.from} onChange={(v) => setEdit((s) => s && { ...s, from: v })} />
        ) : !r.hasCondition || !r.fromSettlementTime ? (
          <span className="text-muted">N/A</span>
        ) : (
          extractTime(r.fromSettlementTime)
        ),
    },
    {
      key: 'to',
      title: 'To Settlement Time',
      value: (r) => extractTime(r.toSettlementTime),
      cell: (r) =>
        isEd(r.symbol) ? (
          <TimePicker value={edit!.to} onChange={(v) => setEdit((s) => s && { ...s, to: v })} />
        ) : !r.hasCondition || !r.toSettlementTime ? (
          <span className="text-muted">N/A</span>
        ) : (
          extractTime(r.toSettlementTime)
        ),
    },
    {
      key: 'avg',
      title: 'Average Daily Volume (M)',
      className: 'text-end',
      value: (r) => r.averageDailyVolume,
      cell: (r) =>
        isEd(r.symbol) ? (
          <div className="input-group input-group-sm"><input type="number" step={0.01} className="form-control" value={edit!.avg} onChange={(e) => setEdit((s) => s && { ...s, avg: e.target.value })} /><span className="input-group-text">M</span></div>
        ) : (
          numDisplay(r.averageDailyVolume, r.hasCondition)
        ),
    },
    {
      key: 'sensitivity',
      title: 'Percentage Sensitivity (%)',
      className: 'text-end',
      value: (r) => r.sensitivityPercent,
      cell: (r) =>
        isEd(r.symbol) ? (
          <div className="input-group input-group-sm"><input type="number" step={0.01} className="form-control" value={edit!.sensitivity} onChange={(e) => setEdit((s) => s && { ...s, sensitivity: e.target.value })} /><span className="input-group-text">%</span></div>
        ) : (
          numDisplay(r.sensitivityPercent, r.hasCondition)
        ),
    },
    {
      key: 'lastModificationTime',
      title: 'Last Updated',
      value: (r) => r.lastModificationTime ?? '',
      cell: (r) => (r.lastModificationTime ? formatDateTime(r.lastModificationTime) : <span className="text-muted">N/A</span>),
    },
  ];

  return (
    <DataGrid
      columns={columns}
      data={rows}
      rowKey={(r) => r.symbol}
      pageSize={5}
      card={false}
      includeExcel
      excelFileName="MAR Instruments"
      emptyMessage={query.isLoading ? 'Loading…' : 'No data.'}
      rowActions={(r) =>
        isEd(r.symbol) ? (
          <span className="d-inline-flex gap-1">
            <button type="button" className="btn btn-outline-primary btn-sm" title="Save" onClick={() => save(r.symbol)} disabled={busy}><i className="fa fa-check" /></button>
            <button type="button" className="btn btn-outline-danger btn-sm" title="Cancel" onClick={() => setEditing(null)} disabled={busy}><i className="fa fa-times" /></button>
          </span>
        ) : (
          <span className="d-inline-flex gap-1 position-relative">
            <button type="button" className="btn btn-outline-info btn-sm position-relative" title="View Notifications" onClick={() => onJump(r.symbol, r.firstNotificationDate)}>
              <i className="fa fa-bell" />
              {r.unvisitedNotificationCount > 0 && <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger">{r.unvisitedNotificationCount}</span>}
            </button>
            <button type="button" className="btn btn-outline-primary btn-sm" title="Edit" onClick={() => startEdit(r)}><i className="fa fa-edit" /></button>
          </span>
        )
      }
    />
  );
}
